import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { PackageSearch, Truck, Package, CreditCard, CheckCircle2, XCircle, Copy } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STAGES = [
  { key: "awaiting_payment", label: "Awaiting Payment", icon: CreditCard },
  { key: "processing", label: "Processing", icon: Package },
  { key: "shipped", label: "Shipped", icon: Truck },
];

export default function TrackOrder() {
  const [params] = useSearchParams();
  const [orderNum, setOrderNum] = useState(params.get("order") || "");
  const [email, setEmail] = useState(params.get("email") || "");
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const lookup = async (e) => {
    if (e) e.preventDefault();
    if (!orderNum.trim() || !email.trim()) return;
    setLoading(true); setErr(""); setResult(null);
    try {
      const { data } = await api.post("/track-order", { order_num: orderNum.trim(), email: email.trim() });
      setResult(data);
    } catch (ex) { setErr(ex.response?.data?.detail || "Something went wrong. Try again."); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (params.get("order") && params.get("email")) lookup(); /* eslint-disable-next-line */ }, []);

  const activeIdx = result ? STAGES.findIndex((s) => s.key === result.stage) : -1;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-20" data-testid="track-order-page">
      <div className="text-center">
        <PackageSearch className="mx-auto text-ape-red mb-5" size={52} />
        <span className="font-mono2 text-xs tracking-[0.3em] text-ape-red">ORDER STATUS</span>
        <h1 className="font-display text-5xl sm:text-6xl tracking-tight mt-2">TRACK YOUR ORDER</h1>
        <p className="text-ape-muted mt-3">Enter your order number and email to see where your order is.</p>
      </div>

      <form onSubmit={lookup} className="mt-10 grid sm:grid-cols-2 gap-3" data-testid="track-order-form">
        <input value={orderNum} onChange={(e) => setOrderNum(e.target.value.toUpperCase())}
          placeholder="Order # e.g. AT-4A320339" data-testid="track-order-number"
          className="bg-transparent border-2 border-ape-border rounded-none px-4 py-3 font-mono2 text-white uppercase focus:border-ape-red focus:outline-none transition-colors duration-150" />
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com" data-testid="track-order-email"
          className="bg-transparent border-2 border-ape-border rounded-none px-4 py-3 font-mono2 text-white focus:border-ape-red focus:outline-none transition-colors duration-150" />
        <button type="submit" disabled={loading} data-testid="track-order-submit"
          className="sm:col-span-2 bg-ape-red hover:bg-ape-redhover disabled:opacity-50 text-white font-head uppercase tracking-widest py-3.5 flex items-center justify-center gap-2 transition-colors duration-150">
          <PackageSearch size={18} /> {loading ? "Searching…" : "Track Order"}
        </button>
      </form>

      {err && (
        <div className="mt-8 border border-ape-red bg-ape-red/10 p-6 flex items-center gap-3" data-testid="track-order-error">
          <XCircle className="text-ape-red shrink-0" size={26} />
          <div><div className="font-head uppercase tracking-wide text-ape-red">Not Found</div><p className="text-ape-muted text-sm mt-1">{err}</p></div>
        </div>
      )}

      {result && (
        <div className="mt-8 border border-ape-border bg-ape-surface" data-testid="track-order-result">
          <div className="p-6 border-b border-ape-border flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="font-mono2 text-[10px] tracking-widest text-ape-muted">ORDER</div>
              <div className="font-display text-2xl tracking-wide">{result.order_num}</div>
            </div>
            <span className={`font-mono2 text-[10px] tracking-widest border px-3 py-1.5 uppercase ${result.status === "paid" ? "border-green-600 text-green-500" : "border-ape-red text-ape-red"}`} data-testid="track-order-status">
              {(result.status || "").replace(/_/g, " ")}
            </span>
          </div>

          {/* Progress */}
          <div className="p-6 border-b border-ape-border">
            <div className="flex items-center justify-between">
              {STAGES.map((s, i) => {
                const done = i <= activeIdx;
                return (
                  <div key={s.key} className="flex-1 flex flex-col items-center relative">
                    {i > 0 && <div className={`absolute top-5 right-1/2 w-full h-0.5 ${i <= activeIdx ? "bg-ape-red" : "bg-ape-border"}`} />}
                    <div className={`relative z-10 h-10 w-10 rounded-full flex items-center justify-center border-2 ${done ? "bg-ape-red border-ape-red text-white" : "bg-ape-bg border-ape-border text-ape-muted"}`}>
                      <s.icon size={18} />
                    </div>
                    <span className={`mt-2 font-mono2 text-[10px] tracking-widest text-center ${done ? "text-white" : "text-ape-muted"}`}>{s.label.toUpperCase()}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Tracking number */}
          <div className="p-6 border-b border-ape-border" data-testid="track-order-tracking">
            <div className="font-mono2 text-[10px] tracking-widest text-ape-muted">TRACKING NUMBER</div>
            {result.tracking_number ? (
              <div className="flex items-center gap-3 mt-1">
                <span className="font-mono2 text-white text-lg">{result.tracking_number}</span>
                {result.carrier && <span className="font-mono2 text-xs text-ape-muted border border-ape-border px-2 py-0.5">{result.carrier}</span>}
                <button onClick={() => { navigator.clipboard.writeText(result.tracking_number); toast.success("Tracking copied"); }}
                  className="text-ape-muted hover:text-ape-red transition-colors duration-150" data-testid="copy-tracking-btn"><Copy size={16} /></button>
              </div>
            ) : (
              <p className="text-ape-muted text-sm mt-1">{result.status === "paid" ? "Your order is being prepared — a tracking number will appear here once it ships." : "Tracking will be available after payment is completed."}</p>
            )}
          </div>

          {/* Items */}
          <div className="p-6 font-mono2 text-sm">
            {result.items?.map((it, i) => (
              <div key={i} className="flex justify-between py-1.5 text-white/90">
                <span>{it.name} ×{it.qty}</span>
                <span>${(it.price * it.qty).toFixed(2)}</span>
              </div>
            ))}
            <div className="flex justify-between pt-3 mt-2 border-t border-ape-border">
              <span className="font-bold text-white">TOTAL</span>
              <span className="font-bold text-ape-red">${(result.total || 0).toFixed(2)}</span>
            </div>
          </div>

          {result.shipping?.full_name && (
            <div className="p-6 border-t border-ape-border font-mono2 text-sm text-white/90" data-testid="track-order-shipping">
              <div className="font-mono2 text-[10px] tracking-widest text-ape-muted mb-2">SHIPPING TO</div>
              <div>{result.shipping.full_name}</div>
              {result.shipping.address && <div>{result.shipping.address}</div>}
              <div>{[result.shipping.city, result.shipping.state, result.shipping.zip].filter(Boolean).join(", ")}</div>
              {result.shipping.country && <div>{result.shipping.country}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
